import isEqual from 'lodash.isequal';
import type { DistributiveOmit } from 'react-redux';
import type { Filters } from '@/features/filters';
import { prepareFilters } from '@/features/filters';
import { DimensionColumnDataType } from '@/entities/columnsConfig';
import { prepareDateRangeValueObject } from '@/shared/api';
import { parseMediaColumnValue } from '@/shared/data';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import { DateRangePresets, DateScale } from '@/shared/types';
import type { SerializedDateRangeValueObject } from '@/shared/types';
import * as ROIBreakdownAPI from '../api/roiBreakdownAPI';
import type { DataConfig, Meta, ROIBreakdownDataResponse } from './types';

type ROIBreakdownRequestedConfig = DistributiveOmit<
  DataConfig,
  'excludedSplitOptions' | 'showRelativeValues'
>;

interface State {
  meta:
    | {
        data: Meta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  config: DataConfig | null;
  data:
    | {
        status: DataStatus.idle | DataStatus.error;
        data: null;
      }
    | {
        status: DataStatus.finished;
        requestedConfig: ROIBreakdownRequestedConfig;
        data: ROIBreakdownDataResponse;
      }
    | {
        status: DataStatus.loading;
        requestedConfig: ROIBreakdownRequestedConfig;
        data: null;
      };
}

const initialState: State = {
  meta: {
    status: DataStatus.idle,
    data: null,
  },
  config: null,
  data: { status: DataStatus.idle, data: null },
};

export const roiBreakdownSlice = createAppSlice({
  name: 'roiBreakdown',
  initialState,
  reducers: (create) => ({
    initMeta: create.asyncThunk(
      (_: void) => {
        return ROIBreakdownAPI.getMeta();
      },
      {
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
          state.meta.data = null;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;

            if (
              isLoading(state.cohortChart.meta.status) ||
              isFinished(state.cohortChart.meta.status)
            ) {
              return false;
            }
          },
        },
      },
    ),
    updateCohortDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.cohortDateScale = action.payload;
    }),
    updateCohortDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        state.config.cohortDateRange = action.payload;
      },
    ),
    updateRevenueDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.revenueDateScale = action.payload;
    }),
    updateRevenueDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        state.config.revenueDateRange = action.payload;
      },
    ),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.probabilisticAttribution = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.filters = action.payload;
    }),
    updateDimension: create.reducer<string>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.dimension = action.payload;
    }),
    updateMetric: create.reducer<string>((state, action) => {
      if (!state.config) {
        return;
      }
      state.config.metric = action.payload;
    }),
    updateShowRelativeValues: create.reducer<boolean>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.showRelativeValues = action.payload;
    }),
    toggleExcludedSplitOption: create.reducer<string>((state, action) => {
      if (!state.config) {
        return;
      }

      const optionIndex =
        state.config.excludedSplitOptions.indexOf(action.payload) ?? -1;

      if (optionIndex !== -1) {
        state.config.excludedSplitOptions =
          state.config.excludedSplitOptions.toSpliced(optionIndex, 1);
      } else {
        state.config.excludedSplitOptions.push(action.payload);
      }
    }),
    updateConfig: create.reducer<DataConfig>((state, action) => {
      state.config = action.payload;
    }),
    getData: create.asyncThunk(
      (chart: DataConfig, { getState }): Promise<ROIBreakdownDataResponse> => {
        const state = getState() as RootState;

        if (!selectMetaIsLoaded(state)) {
          return Promise.reject(new Error('Meta not initialized'));
        }

        const { meta } = state.roiBreakdown;
        const { dimensionsFilters, metricsFilters } = prepareFilters(
          chart.filters,
          meta.data!.metrics,
          meta.data!.dimensions,
        );

        return ROIBreakdownAPI.getData({
          split: chart.dimension,
          metrics: [chart.metric],
          cohortDateScale: chart.cohortDateScale,
          cohortDateRange: prepareDateRangeValueObject(chart.cohortDateRange),
          revenueDateScale: chart.revenueDateScale,
          revenueDateRange: prepareDateRangeValueObject(chart.revenueDateRange),
          probabilisticAttribution: chart.probabilisticAttribution,
          dimensionsFilters,
          metricsFilters,
        }).then((data) => {
          return {
            ...data,
            // options should be sorted in decreasing order for better readability (use case: cohort date as split by option)
            splitOptions: Object.fromEntries(
              Object.entries(data.splitOptions).map(([key, value]) => {
                const sortingOrderSign =
                  meta.data?.config[key].type === DimensionColumnDataType.DATE
                    ? -1
                    : 1;

                return [
                  key,
                  value.sort((a, b) => {
                    return sortingOrderSign * a.localeCompare(b);
                  }),
                ];
              }),
            ),
          };
        });
      },
      {
        pending: (state, action) => {
          state.data = {
            status: DataStatus.loading,
            requestedConfig: getDataRequestedConfig(
              action.meta.arg,
            ) as ROIBreakdownRequestedConfig,
            data: null,
          };
        },
        fulfilled: (state, action) => {
          if (
            (isRequestedConfigExist(state.data) &&
              !isEqual(
                state.data.requestedConfig,
                getDataRequestedConfig(action.meta.arg),
              )) ||
            !state.config
          ) {
            return;
          }

          const previousSplitDimension = isRequestedConfigExist(state.data)
            ? state.data.requestedConfig.dimension
            : null;

          state.data = {
            requestedConfig: isRequestedConfigExist(state.data)
              ? state.data.requestedConfig
              : (getDataRequestedConfig(
                  action.meta.arg,
                ) as ROIBreakdownRequestedConfig),
            status: DataStatus.finished,
            data: action.payload,
          };

          const isMediaContent = selectDimensionIsMedia({
            roiBreakdown: state,
          });
          const { dimension } = state.config;
          const DEFAULT_INCLUDED_OPTIONS_COUNT = 10;
          const optionsToExclude = (
            state.data.data.splitOptions[dimension] || []
          ).slice(DEFAULT_INCLUDED_OPTIONS_COUNT);

          if (previousSplitDimension !== dimension) {
            state.config.excludedSplitOptions = isMediaContent
              ? optionsToExclude.map((option) => {
                  return parseMediaColumnValue(option).value;
                })
              : optionsToExclude;
          }
        },
        rejected: (state, action) => {
          if (
            isRequestedConfigExist(state.data) &&
            !isEqual(
              state.data.requestedConfig,
              getDataRequestedConfig(action.meta.arg),
            )
          ) {
            return;
          }

          state.data.status = DataStatus.error;
        },
      },
    ),
    resetConfig: create.reducer((state) => {
      if (!state.meta.data) {
        return;
      }

      state.config = getDefaultConfig(state.meta.data);
    }),
    resetData: create.reducer((state) => {
      state.data = {
        status: DataStatus.idle,
        data: null,
      };
    }),
    reset: create.reducer(() => {
      return { ...initialState };
    }),
  }),
  selectors: {
    selectMetaDataStatus: (state) => state.meta.status,
    selectMetaIsLoaded: (state) => isFinished(state.meta.status),
    selectMeta: (state) => state.meta.data,
    selectConfig: (state) => {
      return state.config;
    },
    selectData: (state) => state.data,
    selectDimensionIsMedia: (state) => {
      if (!state.meta.data || !state.config) {
        return false;
      }

      return (
        state.meta.data.config[state.config.dimension]?.type ===
        DimensionColumnDataType.MEDIA
      );
    },
    selectDimensionIsDate: (state) => {
      if (!state.meta.data || !state.config) {
        return false;
      }

      return (
        state.meta.data.config[state.config.dimension]?.type ===
        DimensionColumnDataType.DATE
      );
    },
  },
});

const getDefaultConfig = (meta: Meta): DataConfig => {
  return {
    cohortDateScale: DateScale.DAY,
    cohortDateRange: { preset: DateRangePresets.ALL_TIME },
    revenueDateScale: DateScale.DAY,
    revenueDateRange: { preset: DateRangePresets.LAST_90_DAYS },
    probabilisticAttribution: true,
    metric: meta.defaultAxis.metric,
    dimension: meta.defaultAxis.dimension,
    excludedSplitOptions: [],
    showRelativeValues: false,
    filters: [],
  };
};

export function isRequestedConfigExist(data: State['data']): data is
  | {
      status: DataStatus.loading;
      requestedConfig: ROIBreakdownRequestedConfig;
      data: null;
    }
  | {
      status: DataStatus.finished;
      requestedConfig: ROIBreakdownRequestedConfig;
      data: ROIBreakdownDataResponse;
    } {
  return (
    data.status === DataStatus.loading || data.status === DataStatus.finished
  );
}

export const getDataRequestedConfig = (
  config: DataConfig,
): ROIBreakdownRequestedConfig => {
  const { excludedSplitOptions, showRelativeValues, ...otherProps } = config;

  return otherProps;
};

export const {
  initMeta,
  resetConfig,
  updateConfig,
  updateCohortDateScale,
  updateCohortDateRange,
  updateRevenueDateScale,
  updateRevenueDateRange,
  updateProbabilisticAttribution,
  toggleExcludedSplitOption,
  updateFilters,
  updateDimension,
  updateMetric,
  updateShowRelativeValues,
  resetData,
  reset,
  getData,
} = roiBreakdownSlice.actions;
export const {
  selectMetaDataStatus,
  selectMetaIsLoaded,
  selectMeta,
  selectConfig,
  selectData,
  selectDimensionIsMedia,
  selectDimensionIsDate,
} = roiBreakdownSlice.selectors;
